import Box from '@material-ui/core/Box'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Grid from '@material-ui/core/Grid'
import { capitalize } from 'lodash'
import { useState } from 'react'
import { Button } from 'react-admin'
import { Divider } from 'shared/components/Base/Base'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Text } from 'shared/components/Text/Text'
import { formatCurrency, formatDateTime } from 'shared/helpers/formatters'
import { black, green, lightgrey, red } from 'shared/theme/colors'

enum OfferSource {
  DRIVER = 'DRIVER',
  PROVIDER = 'PROVIDER',
}

enum OfferStatus {
  // Driver initially bid on the load
  DRIVER_BID = 'DRIVER_BID',
  // The most recent party to counter is the driver
  DRIVER_COUNTERED = 'DRIVER_COUNTERED',
  // The most recent party to counter is the provider
  PROVIDER_COUNTERED = 'PROVIDER_COUNTERED',
  // The bid has been accepted
  ACCEPTED = 'ACCEPTED',
  // The bid has been rejected
  REJECTED = 'REJECTED',
  // The bid is incomplete
  INCOMPLETE = 'INCOMPLETE',
  // The contactedload has been converted into a job.
  COMPLETE = 'COMPLETE',
}

export type HistoryItem = {
  id: string
  created_by: string
  bid_amount: string
  date_created: string
  current_offer_source?: OfferSource
  status?: OfferStatus | null
  status_reason?: string | null
}

type AutoBidInfoProps = {
  bidMinimum?: string
  bidMaximum?: string
  history?: HistoryItem[]
}

export const AutoBidInfo = ({ bidMinimum, bidMaximum, history }: AutoBidInfoProps) => {
  const [open, setOpen] = useState(false)

  const sortedHistory =
    history &&
    [...history].sort((a, b) => new Date(a.date_created).getTime() - new Date(b.date_created).getTime())
  const mostRecentBid = sortedHistory && sortedHistory[sortedHistory.length - 1]
  const isBidWon =
    mostRecentBid &&
    (mostRecentBid.status === OfferStatus.ACCEPTED || mostRecentBid.status === OfferStatus.COMPLETE)

  const bidMinimumDisplay = bidMinimum ? formatCurrency(bidMinimum) : 'null'
  const bidMaximumDisplay = bidMaximum ? formatCurrency(bidMaximum) : 'null'

  return (
    <>
      <FlexBox justifyContent="space-between" mb={3}>
        <Box>
          <Text variant="label2" fontWeight={700}>
            Auto-bid range: {bidMinimumDisplay}-{bidMaximumDisplay}
          </Text>
          {mostRecentBid &&
            (isBidWon ? (
              <Text variant="label2" fontWeight={700} mt={1} color={green}>
                Bid won: {formatCurrency(mostRecentBid.bid_amount)}
              </Text>
            ) : (
              <Text variant="label2" fontWeight={700} mt={1}>
                Recent auto-bid: {formatCurrency(mostRecentBid.bid_amount)}
              </Text>
            ))}
        </Box>
        <Box mt={-0.5}>
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="text"
              color="primary"
              label="View bid history"
              style={{ fontWeight: 'bold' }}
              disabled={!history || history.length === 0}
              onClick={() => setOpen(true)}
            />
          </Box>
          {mostRecentBid && (
            <Text variant="label2" fontWeight={700} mt={1}>
              {formatDateTime(mostRecentBid.date_created)}
            </Text>
          )}
        </Box>
      </FlexBox>

      <Dialog open={open} fullWidth onClose={() => setOpen(false)}>
        <DialogTitle>Bid History</DialogTitle>
        <DialogContent>
          <FlexBox alignItems="center">
            <Text variant="label2" color="gray" mr={1}>
              Auto-bid range:
            </Text>
            <Text variant="label2" fontWeight={700}>
              {bidMinimumDisplay}-{bidMaximumDisplay}
            </Text>
          </FlexBox>

          <Divider mt={2} mb={4} />

          {sortedHistory &&
            sortedHistory.map((item, index) => {
              const sourceLabel = item.current_offer_source === OfferSource.DRIVER ? 'Autobid' : 'Counter'

              const isGreen = item.status === OfferStatus.ACCEPTED || item.status === OfferStatus.COMPLETE
              const isRed = item.status === OfferStatus.REJECTED
              const isTerminalStatus = isGreen || isRed

              const title = isTerminalStatus
                ? `${sourceLabel} (${capitalize(item.status || '')})`
                : sourceLabel

              return (
                <Grid
                  container
                  key={index}
                  spacing={3}
                  style={{
                    backgroundColor: isTerminalStatus ? lightgrey : 'transparent',
                  }}
                >
                  <Grid item xs={4}>
                    <Text variant="label2" fontWeight={700} color={isGreen ? green : isRed ? red : black}>
                      {title}
                    </Text>
                    {item.status_reason && (
                      <Text variant="label3" color="gray">
                        {item.status_reason}
                      </Text>
                    )}
                  </Grid>
                  <Grid item xs={3}>
                    <Text variant="label2" fontWeight={700}>
                      {formatCurrency(item.bid_amount)}
                    </Text>
                  </Grid>
                  <Grid item xs={5} style={{ textAlign: 'right' }}>
                    <Text variant="label2" fontWeight={700}>
                      {formatDateTime(item.date_created)}
                    </Text>
                  </Grid>
                </Grid>
              )
            })}
        </DialogContent>
        <DialogContent></DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary" label="Close" />
        </DialogActions>
      </Dialog>
    </>
  )
}
